import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Tag, User, ChevronsRight } from "lucide-react";
import StoryCard, { formatDate } from "../components/StoryCard";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { fetchStory, fetchStories } from "../lib/api";

export default function StoryDetail() {
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setStory(null);
    fetchStory(slug)
      .then(async (s) => {
        if (!active) return;
        setStory(s);
        const all = await fetchStories();
        if (active) setRelated(all.filter((x) => x.id !== s.id && x.categories.some((c) => s.categories.includes(c))).slice(0, 3));
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return (
      <section className="max-w-[1000px] mx-auto px-5 py-32" data-testid="story-loading">
        <div className="h-8 w-2/3 bg-[#F8F7F0] rounded animate-pulse" />
        <div className="h-4 w-1/3 bg-[#F8F7F0] rounded animate-pulse mt-4" />
        <div className="h-64 bg-[#F8F7F0] rounded-xl animate-pulse mt-10" />
      </section>
    );
  }

  if (!story) {
    return (
      <section className="max-w-[800px] mx-auto px-5 py-32 text-center">
        <h1 className="text-4xl font-extrabold">Story not found</h1>
        <p className="text-[#878680] mt-4">It may still be under review or the link is incorrect.</p>
        <Link to="/stories" className="btn-green mt-8">Back to Stories</Link>
      </section>
    );
  }

  return (
    <>
      <section className="relative h-[420px] md:h-[520px] overflow-hidden bg-[#24231D]">
        {story.image && <img src={story.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#24231D] via-[#24231D]/40 to-transparent" />
        <div className="relative max-w-[1000px] mx-auto px-5 h-full flex flex-col justify-end pb-14 text-white">
          <Link to="/stories" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-6 fade-up">
            <ArrowLeft size={16} /> All stories
          </Link>
          <div className="flex flex-wrap gap-2 mb-5 fade-up delay-1">
            {story.categories.map((c) => (
              <span key={c} className="bg-[#4BAF47] text-white text-xs font-bold px-3 py-1.5 rounded-full">{c}</span>
            ))}
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight fade-up delay-2">{story.title}</h1>
          <div className="flex flex-wrap gap-6 mt-6 text-sm text-white/80 fade-up delay-3">
            <span className="inline-flex items-center gap-2"><User size={15} className="text-[#4BAF47]" /> {story.author.name}</span>
            <span className="inline-flex items-center gap-2"><Calendar size={15} className="text-[#4BAF47]" /> {formatDate(story.created_at)}</span>
            <span className="inline-flex items-center gap-2"><Tag size={15} className="text-[#4BAF47]" /> {story.categories.length} {story.categories.length === 1 ? "category" : "categories"}</span>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[1000px] mx-auto px-5 py-16 grid lg:grid-cols-3 gap-12">
          <article className="lg:col-span-2">
            {story.image && (
              <figure className="mb-10 -mt-2">
                <img src={story.image} alt={story.title} className="w-full aspect-[16/9] object-cover rounded-xl shadow-[0_30px_60px_-30px_rgba(36,35,29,0.35)]" data-testid="story-featured-image" />
                {story.image_stock && <figcaption className="text-xs text-[#a8a79f] mt-2 text-right">Illustrative photo</figcaption>}
              </figure>
            )}
            <p className="text-xl text-[#5f5e58] font-medium leading-relaxed border-l-4 border-[#4BAF47] pl-5 mb-10">{story.excerpt}</p>
            <div className="story-body" data-testid="story-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: (props) => <a {...props} target="_blank" rel="noreferrer" /> }}>
                {story.content}
              </ReactMarkdown>
            </div>
            {story.source_url && (
              <p className="mt-10 pt-6 border-t border-[#ebe9df] text-sm text-[#878680]">
                Originally published on{" "}
                <a href={story.source_url} target="_blank" rel="noreferrer" className="text-[#4BAF47] font-semibold hover:underline">envirojusticepr.org</a>
                {story.ai_edited && " · Lightly copy-edited for clarity."}
              </p>
            )}
          </article>
          <aside className="space-y-6 self-start lg:sticky lg:top-32">
            <div className="bg-[#F8F7F0] rounded-xl p-7 border border-[#ebe9df]">
              <p className="eyebrow mb-3">About the author</p>
              <div className="flex items-center gap-3">
                <span className="h-12 w-12 rounded-full bg-[#4BAF47] text-white font-extrabold flex items-center justify-center text-lg">
                  {story.author.name.charAt(0)}
                </span>
                <p className="font-extrabold text-lg">{story.author.name}</p>
              </div>
              {story.author.bio && <p className="text-sm text-[#878680] mt-4 leading-relaxed">{story.author.bio}</p>}
            </div>
            <div className="bg-[#24231D] text-white rounded-xl p-7">
              <h3 className="text-xl font-extrabold">Have a story of your own?</h3>
              <p className="text-sm text-[#c9c8c0] mt-2">Inspire others with your environmental success.</p>
              <Link to="/tell-your-story" className="btn-green mt-5 text-sm">Tell Your Story <ChevronsRight size={14} /></Link>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-[#F8F7F0]">
          <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-20">
            <p className="eyebrow mb-3">Keep reading</p>
            <h2 className="section-title mb-10">Related Stories</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((s) => <StoryCard key={s.id} story={s} />)}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
